import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { Spinner, toast, Notifications } from '@intigral/shared';
import { log } from '@intigral/utils';
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appConfig } from '../../config/app.config';
import { STORE_NAME, parentalPinManagementConstants } from '../../config/constants';
import { canUseParentalLockResetOtpFlow } from '../../helpers/parentalLockOtp';
import h from '../../helpers/common';
import url from '../../helpers/url';
import user from '../../helpers/user';
import { required } from '../../helpers/validator';
import { clearVerifyPasswordErrors, verifyPassword } from '../../store/reducers/profileSlice';
import { __ } from '../../translations';

import { Text, FlexWrapper, Button } from '@intigral/shared';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { PasswordInputField } from '@intigral/shared/components/Inputs/PasswordInputField';

const FlexWidth = withPresetWidth(FlexWrapper);

// eslint-disable-next-line @typescript-eslint/no-empty-function
const PasswordPopup = ({
    title,
    description,
    inputLabel,
    inputPlaceholder,
    cancelButtonText,
    buttonText,
    showPopup = false,
    callValidatePassword = true,
    profile = {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onClose = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSuccess = () => {},
    verifyPasswordFunction = verifyPassword,
    canClose = true,
    showForgotPassword = true,
    shouldDispatch = true,
}) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [password, setPassword] = useState('');
    const [passwordFocus, setPasswordFocus] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    // Local loading for shouldDispatch={false} (e.g. SelectProfile switchProfile mutateAsync)
    const [isSubmitting, setIsSubmitting] = useState(false);

    // verifying state
    const { isLoading } = useSelector((state) => state.profile.passwordVerifying);
    const isButtonLoading = isLoading || isSubmitting;

    // verifying actions
    const dispatch = useDispatch();
    const verifyPasswordData = useCallback(
        (password, profile) => dispatch(verifyPasswordFunction({ password, profileData: profile })),
        [dispatch]
    );
    const clearVerifyPasswordErrorsData = useCallback(() => dispatch(clearVerifyPasswordErrors()), [dispatch]);

    const popupRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!popupRef.current) {
            return;
        }
        // disable body scroll and focuse password on show popup
        if (showPopup) {
            disableBodyScroll(popupRef.current);
            setPasswordFocus(true);
        } else {
            enableBodyScroll(popupRef.current);
            clearAllBodyScrollLocks();
            setPasswordFocus(false);
        }
    }, [showPopup]);

    const closePopupHandler = (e = false, redirect = false) => {
        if (e && !redirect) e.preventDefault();

        onClose();
        setPassword('');
        setErrorMessage('');
        setIsSubmitting(false);
        clearVerifyPasswordErrorsData();
    };

    const validateForm = () => {
        if (!required(password)) {
            setErrorMessage(__(B2B_localization + 'password_popup_password_required_error'));
            return false;
        }
        return true;
    };

    const submitHandler = async () => {
        if (isButtonLoading) {
            return;
        }
        if (validateForm()) {
            if (!callValidatePassword) {
                onSubmit(password);
                closePopupHandler();
                return true;
            }
            if (shouldDispatch) {
                try {
                    const isVerified = await verifyPasswordData(password, profile);
                    if (isVerified && !isVerified.error) {
                        onSuccess(password);
                        setPassword('');
                    } else if (typeof isVerified?.payload?.code === 'string') {
                        let errCode = isVerified.payload.code;
                        // The errorCode '9031' is for "Incorrect password", but for B2B the message should be "Incorrect account PIN"
                        const localizedKey = (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}`;
                        let localizedError = __(localizedKey);
                        if (localizedKey === localizedError)
                            // If no localization found, fallback to general error message
                            localizedError = __('general_error_message');
                        toast(localizedError, 'error');
                        setErrorMessage(localizedError);
                    }
                } catch (error) {
                    log.error('Error from password popup', error.message);
                    const localizedKey =
                        (error?.data?.code === '9031' ? B2B_localization : '') + `err_msg_${error?.data?.code}`;
                    let localizedError = __(localizedKey);
                    if (localizedKey === localizedError)
                        // If no localization found, fallback to general error message
                        localizedError = __('general_error_message');
                    toast(localizedError, 'error');
                    setErrorMessage(localizedError);
                }
            } else {
                log.info('no redux');
                setIsSubmitting(true);
                try {
                    await verifyPasswordFunction({ password, profileData: profile });
                } catch (err) {
                    // Toast is handled by the caller's mutation onError (e.g. SelectProfile).
                    // Only set the inline field error here to avoid duplicate toasts.
                    log.error('Error from password popup', err?.message);
                    const errCode = err?.data?.code ?? err?.code;
                    const localizedKey = errCode
                        ? (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}`
                        : '';
                    let localizedError = errCode ? __(localizedKey) : __('general_error_message');
                    if (!errCode || localizedKey === localizedError) {
                        localizedError = __('general_error_message');
                    }
                    setErrorMessage(localizedError);
                } finally {
                    setIsSubmitting(false);
                }
            }
        }
    };

    const inputChangeHandler = (value) => {
        setErrorMessage('');
        setPassword(value);
    };

    const isB2C = user.getUserSegment() === 'B2C';
    const isB2B = user.getUserSegment() === 'B2B';
    const isB2B2C = user.getUserSegment() === 'B2B2C';

    async function resetAccountPIN() {
        setShowSpinner(true);
        const { apiKey, username, identityToken, authToken } = h.getStore(STORE_NAME.USER) || {};
        try {
            const popcornService = container.resolve(DI.SERVICES.IPopcornService);
            const response = await popcornService.forgotPin(apiKey, username, identityToken, authToken);
            toast(
                __(B2B_localization + 'pin_has_been_sent').replace(
                    '{msisdn}',
                    `<span dir="ltr">${response?.msisdn}</span>` ?? ''
                ),
                'success'
            );
        } catch (error) {
            log.error('Error from forgot account PIN', error.message);
            const errCode = error?.data?.code;
            const localizedKey = (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}`;
            let localizedError = __(localizedKey);
            if (localizedKey === localizedError) localizedError = __('general_error_message');
            toast(localizedError, 'error');
        } finally {
            setShowSpinner(false);
        }
    }

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = isB2B ? 'b2b_' : '';

    let forgotPasswordLink = appConfig.forgotPasswordUrl?.replace('{LANG}', h.getLang());

    const getForgotPasswordHref = () => {
        if (canUseParentalLockResetOtpFlow(user.getUserInfo())) {
            return url.route('parental-pin-management', {
                type: window.btoa(`kid-exit|${parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP}`),
            });
        }
        if (isB2C && user.getMSISDN()?.length > 0) {
            return url.route('parental-pin-management', {
                type: window.btoa(`kid-exit|${parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE}`),
            });
        }
        return forgotPasswordLink;
    };

    return (
        <div
            className="container full-width"
            style={{
                display: showPopup ? 'flex' : 'none',
                alignItems: 'center',
                position: 'fixed',
                inset: '0',
                backgroundColor: 'var(--primary-colors-background)',
                // paddingBottom: 'calc(var(--step) * 18)',
                zIndex: '400',
            }}
            ref={popupRef}
        >
            <Spinner style={{ display: isLoading ? 'flex' : 'none' }} />
            <FlexWidth direction="column" gap="4x" alignItems="center" presetWidth="small" style={{ width: '100%' }}>
                <Text as="h2" variant="h2" margin="0" alignment="center">
                    {title || __(B2B_localization + 'password_popup_title')}
                </Text>
                <Text as="span" size="14" style={{ color: 'var(--white-30)' }}>
                    {description || __(B2B_localization + 'password_popup_description_selectProfile')}
                </Text>
                {/* <PasswordPopupInputTextField
                    autoComplete={'off'}
                    type="password"
                    label={inputLabel || __(B2B_localization + 'password_popup_password_label')}
                    placeholder={
                        inputPlaceholder || __(B2B_localization + 'password_popup_password_placeholder')
                    }
                    name="passwordInput"
                    error={errorMessage}
                    value={password}
                    onChange={inputChangeHandler}
                    onSubmit={() => submitHandler()}
                    maxLength={150}
                    autoFocus={passwordFocus}
                /> */}
                <PasswordInputField
                    autoComplete="off"
                    // type="password"
                    label={inputLabel || __(B2B_localization + 'password_popup_password_label')}
                    placeholder={inputPlaceholder || __(B2B_localization + 'password_popup_password_placeholder')}
                    name="passwordInput"
                    invalid={!!errorMessage}
                    errorMessage={errorMessage}
                    value={password}
                    onChange={(e) => inputChangeHandler(e.target.value)}
                    maxLength={150}
                    autoFocus={passwordFocus}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && password.trim().length > 0 && !isButtonLoading) {
                            submitHandler();
                        }
                    }}
                />
                <Notifications />
                {
                    // Hide "Forgot Password" for the routes "/select-profile", "/add-profile" and "/edit-profile"
                    !['select-profile', 'add-profile', 'edit-profile'].includes(url.getCurrentPageName()) &&
                        // Display "Forgot Password" if these conditions met
                        showForgotPassword &&
                        h.isAccountRetrievalFeatureEnabled() && (
                            <a
                                href={getForgotPasswordHref()}
                                onClick={(e) => {
                                    if (isB2B) {
                                        e.preventDefault();
                                        resetAccountPIN();
                                        return;
                                    }
                                    const otpHref = getForgotPasswordHref();
                                    if (
                                        canUseParentalLockResetOtpFlow(user.getUserInfo()) ||
                                        (isB2C && user.getMSISDN()?.length > 0)
                                    ) {
                                        e.preventDefault();
                                        navigate(otpHref);
                                    }
                                }}
                                className="primary-brand"
                            >
                                {__(B2B_localization + 'parental_pin_management_forgot_password')}
                            </a>
                        )
                }
                <FlexWrapper itemCount={2} gap="2x" style={{ width: '100%' }}>
                    <Button
                        secondary
                        onClick={closePopupHandler}
                        data-test="password-cancel"
                        disabled={isButtonLoading}
                    >
                        {cancelButtonText || __('parental_control_pin_dialog_button_cancel')}
                    </Button>
                    <Button
                        onClick={submitHandler}
                        data-test="password-submit"
                        loading={isButtonLoading}
                    >
                        {buttonText || __('password_popup_button')}
                    </Button>
                </FlexWrapper>
                {showSpinner && <Spinner />}
                {/*  // Will use toast instead of this:
                <div className="overlay-warning" style={{ display: errCode ? 'flex' : 'none' }}>
                    <span className="overlay-warning-icon">
                        <i className="icon-Warning" />
                    </span>
                    <p className="overlay-warning-desc">{__(`err_msg_${errCode}`)}</p>
                </div> */}
            </FlexWidth>
        </div>
    );
};

export default PasswordPopup;
